import React, { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';
import './ContactUs.css';

const ContactUs = () => {
  const form = useRef();
  
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    jobTitle: '',
    company: '',
    subject: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  // EmailJS Configuration - Replace with your actual values
  const EMAILJS_CONFIG = {
    PUBLIC_KEY: process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'your_public_key_here',
    SERVICE_ID: process.env.REACT_APP_EMAILJS_SERVICE_ID || 'your_service_id_here',
    TEMPLATE_ID: process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'your_template_id_here'
  };

  const subjectOptions = [
    {
      value: '',
      label: 'Select a subject...',
      message: ''
    },
    {
      value: 'Product or Solution Enquiry',
      label: 'Product or Solution Enquiry',
      message: `I'm interested in the following products or solutions (please remove any that don't apply):

- ThinkFinance
- ThinkHR
- MARS - Market Research
- TAAS - Talent-as-a-Service

*Let us know your specific needs or what you're looking to achieve so we can provide relevant details.*`
    },
    {
      value: 'Demo Request',
      label: 'Demo Request',
      message: `I'm interested in scheduling a demo for the following products or solutions (please remove any that don't apply):

- ThinkFinance
- ThinkHR
- MARS - Market Research (Exploratory meeting)
- TAAS - Talent-as-a-Service (Exploratory meeting)

*Please let us know if any of the listed times work for your team: [insert availability].*`
    },
    {
      value: 'General Enquiry',
      label: 'General Enquiry',
      message: `I couldn't find what I was looking for—can you assist me with the following?`
    },
    {
      value: 'Partnership Opportunities',
      label: 'Partnership Opportunities',
      message: `We're interested in exploring a partnership. Here's a bit about our company and what we have in mind...`
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubjectChange = (e) => {
    const selectedValue = e.target.value;
    const selectedOption = subjectOptions.find(option => option.value === selectedValue);
    
    setFormData(prev => ({
      ...prev,
      subject: selectedValue,
      message: selectedOption ? selectedOption.message : ''
    }));
    
    // Clear errors for both subject and message when subject changes
    if (errors.subject || errors.message) {
      setErrors(prev => ({
        ...prev,
        subject: '',
        message: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }

    return newErrors;
  };

  const sendEmail = async (templateParams) => {
    try {      
      // Check if EmailJS config is properly set
      if (!EMAILJS_CONFIG.PUBLIC_KEY || !EMAILJS_CONFIG.SERVICE_ID || !EMAILJS_CONFIG.TEMPLATE_ID) {
        throw new Error('EmailJS configuration is missing. Please check your environment variables.');
      }

      const response = await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID,
        templateParams,
        EMAILJS_CONFIG.PUBLIC_KEY
      );
      
      console.log('Email sent successfully:', response);
      return { success: true, response };
    } catch (error) {
      console.error('Email send failed:', error);
      return { success: false, error };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      // Prepare template parameters for EmailJS
      const templateParams = {
        from_name: formData.fullName,
        from_email: formData.email,
        subject: formData.subject,
        message: formData.message,
        job_title: formData.jobTitle || 'Not provided',
        company: formData.company || 'Not provided',
        to_email: 'enquiry@intematics.com',
        reply_to: formData.email
      };

      // Send email using EmailJS
      const result = await sendEmail(templateParams);
      
      if (result.success) {
        // Reset form on success
        setFormData({
          fullName: '',
          email: '',
          jobTitle: '',
          company: '',
          subject: '',
          message: ''
        });
        
        setSubmitStatus('success');
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSubmitStatus('');
        }, 5000);
      } else {
        setSubmitStatus('error');
        
        // Clear error message after 5 seconds
        setTimeout(() => {
          setSubmitStatus('');
        }, 5000);
      }
    } catch (error) {
      console.error('Submit error:', error);
      setSubmitStatus('error');
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setSubmitStatus('');
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-us-container">
      {/* Hero Section */}
      <div className="contact-hero">
        <div className="contact-hero-content">
          <h1 className="contact-hero-title">Get In Touch</h1>
          <p className="contact-hero-subtitle">
            We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>
      </div>

      {/* Contact Content */}
      <div className="contact-content">
        <div className="contact-wrapper">
          {/* Contact Info */}
          <div className="contact-info">
            <h2 className="contact-info-title">Contact Information</h2>
            <p className="contact-info-subtitle">
              Reach out to us through any of these channels
            </p>

            <div className="contact-info-items">
              <div className="contact-info-item">
                <div className="contact-info-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M21 10C21 17 12 23 12 23S3 17 3 10C3 6.13401 6.13401 3 10 3H14C17.866 3 21 6.13401 21 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </div>
                <div className="contact-info-text">
                  <h3>Address</h3>
                  <a 
                    href="https://maps.app.goo.gl/VjUpLDnYSmUtz3zk8"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="address-link"
                  >
                    <p>
                      1-3-21 Krystal Point <br />
                      Corporate Park, 11900<br />
                      Bayan Lepas, Pulau Pinang
                      <small style={{display: 'block', marginTop: '4px', color: '#666'}}>
                      📍 Click to view on Google Maps
                    </small>
                    </p>
                  </a>
                </div>
              </div>

              <div className="contact-info-item">
                <div className="contact-info-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="contact-info-text">
                  <h3>Email</h3>
                  <p>enquiry@intematics.com</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="contact-form-container">
            <h2 className="contact-form-title">Send Us a Message</h2>
            
            {submitStatus === 'success' && (
              <div className="alert alert-success">
                <p>Thank you! Your message has been sent successfully. We'll get back to you soon.</p>
              </div>
            )}
            
            {submitStatus === 'error' && (
              <div className="alert alert-error">
                <p>Sorry, there was an error sending your message. Please try again or contact us directly at enquiry@intematics.com</p>
              </div>
            )}

            <form ref={form} className="contact-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="fullName">Full Name *</label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  className={errors.fullName ? 'error' : ''}
                  placeholder="Your full name"
                />
                {errors.fullName && <span className="error-message">{errors.fullName}</span>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={errors.email ? 'error' : ''}
                    placeholder="your.email@example.com"
                  />
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="jobTitle">Job Title</label>
                  <input
                    type="text"
                    id="jobTitle"
                    name="jobTitle"
                    value={formData.jobTitle}
                    onChange={handleInputChange}
                    placeholder="Your job title"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="company">Company</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  placeholder="Your company name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="subject">Subject *</label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleSubjectChange}
                  className={errors.subject ? 'error' : ''}
                >
                  {subjectOptions.map((option, index) => (
                    <option key={index} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.subject && <span className="error-message">{errors.subject}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  rows="5"
                  value={formData.message}
                  onChange={handleInputChange}
                  className={errors.message ? 'error' : ''}
                  placeholder="Tell us more about your inquiry..."
                ></textarea>
                {errors.message && <span className="error-message">{errors.message}</span>}
              </div>

              <button 
                type="submit" 
                className="submit-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;